/**
 * Training Plan Manager (admin)
 *
 * Admin-only management of weekly training plans, shown on the Training page
 * (/skills). Create / edit / delete one plan per (level, week); each week holds
 * a training phase, weekly skill emphasis, and a list of daily sessions.
 *
 * Also supports bulk CSV import per level (e.g. exported from the planning
 * spreadsheet). The current week is computed from the season dates (set in
 * Configuration → Training Season) and is used only to highlight the current
 * week and pre-fill the new-plan week.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { restClient } from '../../../api/rest-client';
import { Button } from '../../common/Button';
import { LoadingSpinner } from '../../common/LoadingSpinner';
import { Modal } from '../../common/Modal';
import { useUIStore } from '../../../store/uiStore';
import {
  TRAINING_PLAN_LEVELS,
  DAYS_OF_WEEK,
  emptySession,
  type DayOfWeek,
  type TrainingPlan,
  type TrainingPlanLevel,
  type TrainingPlanSession,
  type UpsertTrainingPlanDto,
} from '../../../types/trainingPlans';

const LEVEL_HEADING_CLASSES: Record<TrainingPlanLevel, string> = {
  Beginner: 'text-orange-700',
  Intermediate: 'text-blue-700',
  Advanced: 'text-green-700',
};

const INPUT_CLASSES =
  'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500';

const SUB_LABEL_CLASSES = 'block text-xs font-medium text-gray-600 mb-1';

interface TrainingPlanFormProps {
  plan?: TrainingPlan;
  defaultWeek: number;
  onSubmit: (data: UpsertTrainingPlanDto) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
}

function TrainingPlanForm({ plan, defaultWeek, onSubmit, onCancel, isLoading }: TrainingPlanFormProps) {
  const isEditing = !!plan;
  const [level, setLevel] = useState<TrainingPlanLevel>(plan?.level ?? 'Beginner');
  const [week, setWeek] = useState<number>(plan?.week ?? defaultWeek);
  const [trainingPhase, setTrainingPhase] = useState(plan?.training_phase ?? '');
  const [skillEmphasis, setSkillEmphasis] = useState(plan?.skill_emphasis ?? '');
  const [sessions, setSessions] = useState<TrainingPlanSession[]>(
    plan?.sessions?.length ? plan.sessions.map((s) => ({ ...s })) : [emptySession('Mon')],
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  const updateSession = (index: number, field: keyof TrainingPlanSession, value: string) => {
    setSessions((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [field]: field === 'day_of_week' ? (value as DayOfWeek) : value } : s)),
    );
  };

  const addSession = () => setSessions((prev) => [...prev, emptySession('Mon')]);
  const removeSession = (index: number) => setSessions((prev) => prev.filter((_, i) => i !== index));

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!Number.isInteger(week) || week < 1) next.week = 'Week must be a positive whole number';
    if (sessions.length === 0) next.sessions = 'Add at least one session';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    await onSubmit({
      level,
      week,
      training_phase: trainingPhase.trim(),
      skill_emphasis: skillEmphasis.trim(),
      sessions: sessions.map((s) => ({
        day_of_week: s.day_of_week,
        periodization_focus: s.periodization_focus.trim(),
        session_type: s.session_type.trim(),
        target_rpe: s.target_rpe.trim(),
        prescribed_workout: s.prescribed_workout.trim(),
        coaches_note: s.coaches_note.trim(),
      })),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="level" className="block text-sm font-medium text-gray-700 mb-1">
            Level *
          </label>
          <select
            id="level"
            value={level}
            onChange={(e) => setLevel(e.target.value as TrainingPlanLevel)}
            disabled={isEditing}
            className={`${INPUT_CLASSES} disabled:bg-gray-100`}
            aria-label="Training plan level"
          >
            {TRAINING_PLAN_LEVELS.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="week" className="block text-sm font-medium text-gray-700 mb-1">
            Week *
          </label>
          <input
            type="number"
            id="week"
            min={1}
            value={week}
            onChange={(e) => setWeek(Number(e.target.value))}
            disabled={isEditing}
            className={`${INPUT_CLASSES} disabled:bg-gray-100 ${errors.week ? 'border-red-500' : ''}`}
            aria-label="Week number"
          />
          {errors.week && <p className="mt-1 text-sm text-red-600">{errors.week}</p>}
        </div>
      </div>

      <div>
        <label htmlFor="training_phase" className="block text-sm font-medium text-gray-700 mb-1">
          Training Phase
        </label>
        <input
          type="text"
          id="training_phase"
          value={trainingPhase}
          onChange={(e) => setTrainingPhase(e.target.value)}
          className={INPUT_CLASSES}
          placeholder="e.g., Base 1"
          aria-label="Training phase"
        />
      </div>

      <div>
        <label htmlFor="skill_emphasis" className="block text-sm font-medium text-gray-700 mb-1">
          Weekly Skill Emphasis
        </label>
        <input
          type="text"
          id="skill_emphasis"
          value={skillEmphasis}
          onChange={(e) => setSkillEmphasis(e.target.value)}
          className={INPUT_CLASSES}
          placeholder="e.g., Braking mechanics & basic modulation"
          aria-label="Weekly skill emphasis"
        />
      </div>

      {/* Sessions */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-800">Daily Sessions</span>
          <button
            type="button"
            onClick={addSession}
            className="rounded px-2 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 hover:text-primary-800"
            aria-label="Add session"
          >
            + Add Session
          </button>
        </div>
        {errors.sessions && <p className="mb-2 text-sm text-red-600">{errors.sessions}</p>}

        <div className="space-y-4">
          {sessions.map((s, index) => (
            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <label htmlFor={`day-${index}`} className="text-xs font-medium text-gray-600">
                    Day
                  </label>
                  <select
                    id={`day-${index}`}
                    value={s.day_of_week}
                    onChange={(e) => updateSession(index, 'day_of_week', e.target.value)}
                    className="rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    aria-label={`Session ${index + 1} day`}
                  >
                    {DAYS_OF_WEEK.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
                {sessions.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeSession(index)}
                    className="rounded px-2 py-1 text-xs text-red-500 hover:bg-red-50 hover:text-red-700"
                    aria-label={`Remove session ${index + 1}`}
                  >
                    Remove
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div>
                  <label htmlFor={`periodization-${index}`} className={SUB_LABEL_CLASSES}>
                    Periodization Focus
                  </label>
                  <input
                    type="text"
                    id={`periodization-${index}`}
                    value={s.periodization_focus}
                    onChange={(e) => updateSession(index, 'periodization_focus', e.target.value)}
                    className={INPUT_CLASSES}
                    placeholder="e.g., Aerobic Endurance"
                  />
                </div>
                <div>
                  <label htmlFor={`session-type-${index}`} className={SUB_LABEL_CLASSES}>
                    Session Type
                  </label>
                  <input
                    type="text"
                    id={`session-type-${index}`}
                    value={s.session_type}
                    onChange={(e) => updateSession(index, 'session_type', e.target.value)}
                    className={INPUT_CLASSES}
                    placeholder="e.g., Segmented Steady State"
                  />
                </div>
                <div>
                  <label htmlFor={`target-rpe-${index}`} className={SUB_LABEL_CLASSES}>
                    Target RPE
                  </label>
                  <input
                    type="text"
                    id={`target-rpe-${index}`}
                    value={s.target_rpe}
                    onChange={(e) => updateSession(index, 'target_rpe', e.target.value)}
                    className={INPUT_CLASSES}
                    placeholder="e.g., RPE 3-4"
                  />
                </div>
              </div>

              <div className="mt-2">
                <label htmlFor={`workout-${index}`} className={SUB_LABEL_CLASSES}>
                  Prescribed Workout
                </label>
                <textarea
                  id={`workout-${index}`}
                  rows={2}
                  value={s.prescribed_workout}
                  onChange={(e) => updateSession(index, 'prescribed_workout', e.target.value)}
                  className={INPUT_CLASSES}
                  placeholder="e.g., 15m static braking drill. 30m trail application..."
                />
              </div>
              <div className="mt-2">
                <label htmlFor={`note-${index}`} className={SUB_LABEL_CLASSES}>
                  Coaches Note
                </label>
                <textarea
                  id={`note-${index}`}
                  rows={2}
                  value={s.coaches_note}
                  onChange={(e) => updateSession(index, 'coaches_note', e.target.value)}
                  className={INPUT_CLASSES}
                  placeholder="e.g., Watch for locking up rear tires..."
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <Button type="submit" variant="primary" disabled={isLoading} className="flex-1" aria-label={isEditing ? 'Update plan' : 'Create plan'}>
          {isLoading ? 'Saving...' : isEditing ? 'Update' : 'Create'}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading} aria-label="Cancel">
          Cancel
        </Button>
      </div>
    </form>
  );
}

export function TrainingPlanManager() {
  const showToast = useUIStore((s) => s.showToast);
  const navigate = useNavigate();

  const [plans, setPlans] = useState<TrainingPlan[]>([]);
  const [season, setSeason] = useState<string>('');
  const [currentWeek, setCurrentWeek] = useState<number>(1);
  const [totalWeeks, setTotalWeeks] = useState<number>(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<TrainingPlan | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<TrainingPlan | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await restClient.getTrainingPlans();
      setPlans(data.plans);
      setSeason(data.season);
      setCurrentWeek(data.current_week);
      setTotalWeeks(data.total_weeks);
    } catch (err) {
      console.error('Failed to fetch training plans:', err);
      setError(err instanceof Error ? err.message : 'Failed to load training plans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAdd = () => {
    setEditingPlan(undefined);
    setIsFormOpen(true);
  };

  const handleEdit = (plan: TrainingPlan) => {
    setEditingPlan(plan);
    setIsFormOpen(true);
  };

  const handleFormSubmit = useCallback(
    async (data: UpsertTrainingPlanDto) => {
      setIsSubmitting(true);
      try {
        const payload = { ...data, season: season || undefined };
        if (editingPlan) {
          await restClient.updateTrainingPlan(payload);
          showToast('success', 'Training plan updated');
        } else {
          await restClient.createTrainingPlan(payload);
          showToast('success', 'Training plan created');
        }
        await fetchData();
        setIsFormOpen(false);
        setEditingPlan(undefined);
      } catch (err) {
        console.error('Failed to save training plan:', err);
        showToast('error', err instanceof Error ? err.message : 'Failed to save training plan');
      } finally {
        setIsSubmitting(false);
      }
    },
    [editingPlan, season, fetchData, showToast],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsSubmitting(true);
    try {
      await restClient.deleteTrainingPlan({
        season: season || undefined,
        level: deleteTarget.level,
        week: deleteTarget.week,
      });
      showToast('success', 'Training plan deleted');
      await fetchData();
      setDeleteTarget(null);
    } catch (err) {
      console.error('Failed to delete training plan:', err);
      showToast('error', err instanceof Error ? err.message : 'Failed to delete training plan');
    } finally {
      setIsSubmitting(false);
    }
  }, [deleteTarget, season, fetchData, showToast]);

  // Group plans by level, each sorted by week ascending
  const plansByLevel = useMemo(() => {
    const map = new Map<TrainingPlanLevel, TrainingPlan[]>();
    for (const level of TRAINING_PLAN_LEVELS) map.set(level, []);
    for (const plan of plans) {
      const list = map.get(plan.level);
      if (list) list.push(plan);
    }
    for (const list of map.values()) list.sort((a, b) => a.week - b.week);
    return map;
  }, [plans]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-semibold text-primary-900">Manage Training Plans</h2>
          <span className="text-sm text-gray-500">
            Week {currentWeek} of {totalWeeks}
            {season ? ` · Season ${season}` : ''}
          </span>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => navigate('/skills/import')} aria-label="Import from CSV">
            Import CSV
          </Button>
          <Button variant="primary" onClick={handleAdd} aria-label="Add training plan">
            Add Plan
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <div className="space-y-8">
        {TRAINING_PLAN_LEVELS.map((level) => {
          const levelPlans = plansByLevel.get(level) ?? [];
          return (
            <div key={level}>
              <h3 className={`text-lg font-semibold mb-3 ${LEVEL_HEADING_CLASSES[level]}`}>
                {level}
                <span className="ml-2 text-sm font-normal text-gray-400">
                  {levelPlans.length} week{levelPlans.length !== 1 ? 's' : ''}
                </span>
              </h3>

              {levelPlans.length === 0 ? (
                <p className="text-sm text-gray-500">No plans yet for {level}.</p>
              ) : (
                <div className="space-y-2">
                  {levelPlans.map((plan) => (
                    <div
                      key={`${plan.level}-${plan.week}`}
                      className={`flex items-start justify-between rounded-md border p-3 ${
                        plan.week === currentWeek ? 'border-primary-300 bg-primary-50' : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="min-w-0 flex-1 mr-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs font-semibold text-gray-500">Week {plan.week}</span>
                          {plan.week === currentWeek && (
                            <span className="rounded bg-primary-100 px-1.5 py-0.5 text-xs font-medium text-primary-700">
                              Current
                            </span>
                          )}
                          <span className="text-xs text-gray-400">
                            {plan.sessions.length} session{plan.sessions.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        {plan.training_phase && (
                          <p className="mt-0.5 text-sm font-medium text-gray-900">{plan.training_phase}</p>
                        )}
                        {plan.skill_emphasis && <p className="text-sm text-gray-600">{plan.skill_emphasis}</p>}
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <button
                          onClick={() => handleEdit(plan)}
                          className="rounded px-2 py-1 text-xs text-primary-600 transition-colors hover:bg-primary-50 hover:text-primary-800"
                          aria-label={`Edit ${level} week ${plan.week}`}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleteTarget(plan)}
                          className="rounded px-2 py-1 text-xs text-red-500 transition-colors hover:bg-red-50 hover:text-red-700"
                          aria-label={`Delete ${level} week ${plan.week}`}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Create/Edit modal */}
      <Modal
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingPlan(undefined);
        }}
        title={editingPlan ? `Edit ${editingPlan.level} — Week ${editingPlan.week}` : 'Add Training Plan'}
      >
        <TrainingPlanForm
          plan={editingPlan}
          defaultWeek={currentWeek}
          onSubmit={handleFormSubmit}
          onCancel={() => {
            setIsFormOpen(false);
            setEditingPlan(undefined);
          }}
          isLoading={isSubmitting}
        />
      </Modal>

      {/* Delete confirmation */}
      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Training Plan">
        <div className="space-y-4">
          <p className="text-gray-700">
            Delete the <strong>{deleteTarget?.level}</strong> plan for{' '}
            <strong>Week {deleteTarget?.week}</strong>
            {deleteTarget?.training_phase ? ` (${deleteTarget.training_phase})` : ''}?
          </p>
          <p className="text-sm text-red-600">This action cannot be undone.</p>
          <div className="flex gap-3 pt-2">
            <Button variant="danger" onClick={handleConfirmDelete} disabled={isSubmitting} className="flex-1" aria-label="Confirm delete">
              {isSubmitting ? 'Deleting...' : 'Delete'}
            </Button>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={isSubmitting} aria-label="Cancel delete">
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </section>
  );
}
